import { initSentry, Sentry } from '@storm/core';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './app.js';
import { initAuth } from './game/auth.js';
import './styles.css';
import './styles/sirus-tokens.css';

initSentry('aiwari');

// Warm the spawn tile while the player is still on the login screen: the
// Northshire tile JSON is ~1.2 MB gz and sits on the world-load critical
// path. By enter-world time it's already in the browser cache.
if (typeof window !== 'undefined') {
  try {
    const v = typeof __BUILD_ID__ === 'string' ? encodeURIComponent(__BUILD_ID__) : '0';
    void fetch(`${import.meta.env.BASE_URL}data/adt/Azeroth_32_48.json?v=${v}`, {
      cache: 'default', priority: 'low',
    } as RequestInit).catch(() => { /* prefetch is best-effort */ });
  } catch { /* ignore */ }
}

// Tag every Sentry event with the live AIWARI session context so I can
// see exactly what character / class / side / what they were doing when
// the error fired. Updated whenever the realm-store / UI-store changes.
if (typeof window !== 'undefined') {
  void (async () => {
    try {
      const { useUI } = await import('./state/store.js');
      const { useNet } = await import('./state/net-store.js');
      const apply = () => {
        const draft = useUI.getState().draft;
        const net = useNet.getState();
        const self = net.selfId ? net.players[net.selfId] : null;
        Sentry.setUser({
          id: net.selfId ?? draft.name ?? 'anon',
          username: draft.name ?? 'anon',
        });
        Sentry.setTags({
          'aiwari.class': draft.classId ?? 'none',
          'aiwari.side': draft.side ?? 'none',
          'aiwari.subFaction': draft.subFactionId ?? 'none',
          'aiwari.connected': String(net.connected),
          'aiwari.level': String(self?.level ?? '-'),
          'aiwari.dead': String(!!(self as { dead?: boolean } | null)?.dead),
        });
        Sentry.setContext('aiwari.live', {
          stage: useUI.getState().stage,
          mobsLoaded: Object.keys(net.mobs).length,
          playersLoaded: Object.keys(net.players).length,
          selfHp: self ? `${self.hp}/${self.hpMax}` : '-',
          selfPos: self ? `(${self.pos.x.toFixed(1)}, ${self.pos.z.toFixed(1)})` : '-',
        });
      };
      apply();
      useUI.subscribe(apply);
      useNet.subscribe(apply);
    } catch {}
  })();
  // Also drop a breadcrumb on every aiwari ws message via a shim.
  window.addEventListener('aiwari-debug', (e) => {
    const detail = (e as CustomEvent).detail as { kind: string; data?: unknown };
    Sentry.addBreadcrumb({
      category: 'aiwari',
      level: 'info',
      message: detail.kind,
      data: detail.data as Record<string, unknown> | undefined,
    });
  });
}

initAuth();

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element not found');

createRoot(rootEl).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

// R232q5h — Service Worker DISABLED.  Prior SW iterations (v1/v2/v3)
// accumulated stale cache-first content that turned the live game into
// "old laggy crap" for users who'd visited during dev.  Inline kill-switch
// in index.html aggressively unregisters any residual SW + nukes the
// aiwari-* Cache Storage keys.  No new registration here so the page stays
// clean; we rely on plain HTTP caching (browser disk + nginx headers).
// To re-enable later, restore the previous register block below.
